// Data layer: fetch weekly OHLCV from the production worker and expose it as a
// clean {t, close} series. Weekly bars carry full history (period1=0 upstream),
// so a 200-week moving average always has enough data.
//
// The worker returns adjusted closes in the `c` field (dividends reinvested for
// ETFs; price for indices/futures; yield level for the ^TNX/^TYX rate indices).
// Yahoo blocks residential IPs, so dev proxies /api/* to the production worker
// (see vite.config.ts) — same constraint as the other MarketHeist apps.

const WORKER_URL: string = (import.meta as any).env?.DEV
  ? ""
  : ((import.meta as any).env?.VITE_WORKER_URL ?? "https://api.marketheist.io");

/** Owner-defined asset-class grouping (used for the row tag and the class tabs). */
export type AssetCategory = "Equities" | "Crypto" | "Currency" | "Commodity" | "Bonds" | "Interest Rates";

/** One asset class shown on the Context dashboard. */
export interface AssetDef {
  /** Stable key used for React keys and ranking. */
  key: string;
  /** Public-friendly label. */
  label: string;
  /** Yahoo Finance symbol fetched from the worker. */
  symbol: string;
  /** What the `close` field represents — drives value formatting. */
  kind: "equity" | "index" | "commodity" | "bond" | "rate";
  /** Asset-class label shown as a tag on the row (owner-defined grouping). */
  category: AssetCategory;
  /** One-line plain-English description of the instrument. */
  blurb: string;
}

// Futures + yield indices (owner-selected). Rates are yield levels, so their
// "price vs MA" read is a rising- vs falling-yield trend, not a price trend.
// `category` is the owner-defined asset-class grouping (independent of `kind`,
// which only drives number formatting — e.g. gold is a Currency but prices in $).
export const ASSETS: AssetDef[] = [
  { key: "ndx",   label: "Nasdaq 100",       symbol: "^NDX",     kind: "equity",    category: "Equities",       blurb: "US large-cap growth & tech" },
  { key: "spx",   label: "S&P 500",          symbol: "^GSPC",    kind: "equity",    category: "Equities",       blurb: "US large-cap broad market" },
  { key: "rut",   label: "Russell 2000",     symbol: "^RUT",     kind: "equity",    category: "Equities",       blurb: "US small-cap" },
  { key: "gold",  label: "Gold",             symbol: "GC=F",     kind: "commodity", category: "Currency",        blurb: "Gold futures (COMEX)" },
  { key: "dxy",   label: "US Dollar Index",  symbol: "DX-Y.NYB", kind: "index",     category: "Currency",        blurb: "USD vs a basket of major currencies" },
  { key: "crude", label: "Crude Oil",        symbol: "BZ=F",     kind: "commodity", category: "Commodity",       blurb: "Brent crude futures (ICE)" },
  { key: "btc",   label: "Bitcoin",          symbol: "BTC-USD",  kind: "commodity", category: "Crypto",          blurb: "Bitcoin priced in US dollars" },
  { key: "eth",   label: "Ethereum",         symbol: "ETH-USD",  kind: "commodity", category: "Crypto",          blurb: "Ethereum priced in US dollars" },
  { key: "sol",   label: "Solana",           symbol: "SOL-USD",  kind: "commodity", category: "Crypto",          blurb: "Solana priced in US dollars" },
  { key: "tlt",   label: "Long Treasuries",  symbol: "TLT",      kind: "bond",      category: "Bonds",           blurb: "20+ year US Treasury bonds" },
  { key: "mub",   label: "Municipal Bonds",  symbol: "MUB",      kind: "bond",      category: "Bonds",           blurb: "National investment-grade munis" },
  { key: "hyg",   label: "High-Yield Bonds", symbol: "HYG",      kind: "bond",      category: "Bonds",           blurb: "High-yield corporate bonds" },
  { key: "ust10", label: "10-Year Rates", symbol: "^TNX", kind: "rate", category: "Interest Rates", blurb: "10-year US Treasury yield" },
  { key: "ust30", label: "30-Year Rates", symbol: "^TYX", kind: "rate", category: "Interest Rates", blurb: "30-year US Treasury yield" },
];

export interface CloseSeries {
  /** Bar close timestamps (epoch milliseconds), ascending. */
  t: number[];
  /** Adjusted close per bar, ascending. */
  close: number[];
}

interface OhlcvRow { t: number; o: number; h: number; l: number; c: number; v: number }
interface OhlcvResponse { ticker: string; frequency: string; rows: OhlcvRow[]; shortName?: string }

export class DataError extends Error {}

const cache = new Map<string, Promise<CloseSeries>>();

// Persistent per-symbol cache. Weekly full history barely changes — one new bar
// a week, and only the still-forming current bar moves intraday — so a fresh
// localStorage entry lets a reload paint instantly with zero /api/ohlcv calls.
// 12h is a bit fresher than the worker's 1wk KV cache (1 day) — a device never
// shows weekly data older than 12h, and a client miss just hits the worker's
// (usually warm) cache. One key PER SYMBOL so a single corrupt/oversized entry
// can be dropped without nuking the rest.
const FREQ = "1wk";
const LS_PREFIX = "ctx.ohlcv.v1";
const LS_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

interface CachedEntry { savedAt: number; data: CloseSeries }

function lsKey(symbol: string): string {
  return `${LS_PREFIX}.${FREQ}.${symbol}`;
}

/** Read a fresh, well-formed cached series, or null. Never throws — localStorage
 *  can be blocked (private mode) or hold a corrupt/legacy value. */
function readLsCache(symbol: string): CloseSeries | null {
  try {
    const raw = localStorage.getItem(lsKey(symbol));
    if (!raw) return null;
    const entry = JSON.parse(raw) as CachedEntry;
    const d = entry?.data;
    if (
      typeof entry?.savedAt !== "number" ||
      !d || !Array.isArray(d.t) || !Array.isArray(d.close) ||
      d.t.length === 0 || d.t.length !== d.close.length
    ) return null;
    if (Date.now() - entry.savedAt > LS_TTL_MS) return null; // expired
    return { t: d.t, close: d.close };
  } catch {
    return null;
  }
}

/** Persist a series (best-effort). Never throws — quota/blocked storage is fine. */
function writeLsCache(symbol: string, data: CloseSeries): void {
  try {
    localStorage.setItem(lsKey(symbol), JSON.stringify({ savedAt: Date.now(), data }));
  } catch {
    /* private mode / quota exceeded / blocked — caching is best-effort */
  }
}

/** Fetch one symbol's weekly close series. Served from (1) an in-memory Map, then
 *  (2) a 12h localStorage cache, then (3) the worker. Failed/empty responses are
 *  never cached, so a transient error is retryable. */
export function fetchWeeklyCloses(symbol: string): Promise<CloseSeries> {
  const key = symbol.toUpperCase();
  let p = cache.get(key);
  if (!p) {
    p = (async () => {
      // Persistent layer: a fresh entry skips the network entirely.
      const stored = readLsCache(key);
      if (stored) return stored;

      const url = `${WORKER_URL}/api/ohlcv?ticker=${encodeURIComponent(key)}&frequency=${FREQ}`;
      const resp = await fetch(url);
      if (!resp.ok) {
        // The worker's sentence says whether a retry can help (503 = provider
        // temporarily unavailable). The board itself shows "reload to retry".
        const body = (await resp.json().catch(() => null)) as { error?: unknown } | null;
        throw new DataError(typeof body?.error === "string" && body.error ? body.error : `No data for "${key}" (${resp.status}).`);
      }
      const body = (await resp.json()) as OhlcvResponse;
      const rows = (body.rows ?? []).filter((r) => Number.isFinite(r.c) && r.c > 0);
      if (rows.length === 0) throw new DataError(`No data for "${key}".`);
      rows.sort((a, b) => a.t - b.t);
      const series: CloseSeries = { t: rows.map((r) => r.t), close: rows.map((r) => r.c) };
      writeLsCache(key, series); // only successful, non-empty responses reach here
      return series;
    })();
    p.catch(() => cache.delete(key));
    cache.set(key, p);
  }
  return p;
}
